/**
 * @module app.ext.iqc
 */

import { autoinject } from 'aurelia-framework';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { DateValue } from 'app/shared/lib/date-time';
import { deepClone, isNullOrUndefined } from 'app/shared/lib/utilities';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { AuthExternalSystemChangeEvent, AuthExternalSystemErrorEvent, AuthIQCGuidelinesSearchEvent, AuthIQCGuidelinesSearchTimeoutEvent, AuthIQCMedicalReviewEvent } from 'app/um/lib/events';
import { IApiRequest, IApiSettings } from 'app/shared/lib/api/api-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { I18N } from 'aurelia-i18n';
import { SessionChangedEvent } from 'app/shared/lib/session/session-events';
import { EnvironmentService } from 'app/shared/services/environment-service';
import * as api from 'app/shared/lib/essette-api';
import * as UM from 'app/um/models/essette-api';

export const APPROVED_DETERMINATION_TYPES = ['approved', 'modified'];
export const NOT_APPLICABLE_CRITERIA_STATUS = 'Not Applicable';
export const EXTERNAL_SYSTEM_CODE = 'IQ';
export const MAPPING_CODES = {
    CPT: 'CPT',
    HCPCS: 'HCPCS'
};

interface IMRCollection {
    type: 'MRCollection';
    name: 'PrimaryRef' | 'SecondaryRef';
    value: string;
}

interface IPopupParameters {
    newReview?: {
        guidelineId?: string,
        bookview?: boolean
    },
    guidelineRestricted?: boolean,
    reviewId?: string,
    copyReview?: boolean,
    openReviewSummaryPdf?: boolean;
    diag?: string,
    svc?: string,
    careDayId?: string,
    memberFirstName?: string,
    memberLastName?: string,
    memberDOB?: string,
    memberGender?: string,
    externalId?: string,
    additionalIds?: additionalId[],
    serviceState?: string,
    serviceDate?: string,
    cptCodes?: string[],
    hcpcsCodes?: string[],
    contextParams?: IMRCollection[],
    hideSaveReviewButton?: boolean,
}

interface IIQFrameParameters {
    samlAssertion?: string;
    serviceCodes?: string[],
    diagnosisCodes?: string[],
    serviceState?: string,
    serviceDate?: string
    age?: string;
}

interface additionalId {
    type: string,
    id: string
}

export interface IOpenIQCParameters {
    auth?: UM.Auth;
    review?: UM.AuthMedicalReview;
    diagnosis?: UM.AuthDiagnosis;
    services?: UM.AuthService[];
    careDay?: UM.AdmissionCareDay;
    splitCodes?: boolean;
    useDynamicSso?: boolean;
    useRestrictedMode?: boolean;
    allowReviewCopy?: boolean;
    openReviewSummaryPdf?: boolean;
    hideSaveReviewButton?: boolean;
}

type IqcGender = 'MALE' | 'FEMALE' | 'OTHER';

/**
 * Map a gender code to one of the valid IQC genders (`MALE`, `FEMALE`, or `OTHER`)
 * @param code a value of `M` or `F` for Male or Female, otherwise the result will be Other
 * @returns a gender code for IQC
 */
function mapIqcGenderCode(code?: string | null): IqcGender {
    if(code === 'F' || code === 'f') { return 'FEMALE'; }
    if(code === 'M' || code === 'm') { return 'MALE'; }
    return 'OTHER';
}

/**
 * Format a date (as `MM-dd-yyyy`) for passing to the IQC API
 * @param dateValue
 * @returns a date in the proper format
 */
function formatIqcDate(dateValue: DateValue): string {
    if(!dateValue.isValid) { return ''; }
    const { month, date, year } = dateValue.toParts();
    return `${String(month).padStart(2, '0')}-${String(date).padStart(2, '0')}-${String(year).padStart(4, '0')}`;
}

@autoinject
export class IqcManager {

    ////****************** Fields and Properties ************************************////
    private interQualConnectUrl: string | null = null;
    private javascriptSourceUrl: string | null = null;
    private editedReview: UM.AuthMedicalReview = {};

    private showPHI: boolean = false;
    private auth: UM.Auth | undefined = undefined;
    private review: UM.AuthMedicalReview | undefined = undefined;
    private diagnosis: UM.AuthDiagnosis | undefined = undefined;
    private services: UM.AuthService[] | undefined = undefined;
    private careDay: UM.AdmissionCareDay | undefined = undefined;
    private settings: IApiSettings = {};
    private sessionEvent: Subscription | null = null;
    private guidelineIds: string | undefined = undefined;
    // The service/diagnosis code associated with the guideline Ids found
    private guidelineCode: string | undefined = undefined;
    private timeoutId: any = null;

    private listener: (event: MessageEvent) => {};


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly api: api.Api,
        private readonly env: EnvironmentService,
        private readonly events: EventAggregator,
        private readonly logger: AppLogger,
        private readonly i18n: I18N) {

        this.logger.source = this;
        this.loadSettings();

        // Need to assign listener function to a class variable, so that it is only created and assigned once, and therefore can be added and removed as a window listener function
        this.listener = this.handleEventMessage.bind(this);
        window.addEventListener('message', this.listener, false);

        this.sessionEvent = this.events.subscribe(SessionChangedEvent,
            (e: SessionChangedEvent) => {
                if(['checkIn', 'logIn'].includes(e.key)) {
                    // if user context has changed (tenant, context) due to logIn/logOut/checkIn/etc.
                    this.loadSettings();
                }
            });
    }

    protected deactivate(): void {
        if(this.sessionEvent) {
            this.sessionEvent.dispose();
        }
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async loadSettings(): Promise<void> {
        if(this.env.tenant.id) {
            this.settings = await this.loadSystemSettings();
            const consumerServiceUrl = <string>getApiSetting(this.settings, 'interQualConnect.saml.consumerServiceUrl.$url');
            if(consumerServiceUrl) this.interQualConnectUrl = consumerServiceUrl;
            else
                this.logger.error('Unable to set consumerServiceUrl');
            const javascriptSourceUrl = <string>getApiSetting(this.settings, 'interQualConnect.javascript.sourceUrl.$url');
            if(javascriptSourceUrl) this.javascriptSourceUrl = javascriptSourceUrl;
            else
                this.logger.error('Unable to set javascriptSourceUrl');
            if(<boolean>getApiSetting(this.settings, 'interQualConnect.submitMemberInformation.$isEnabled') === true) {
                this.showPHI = true;
            }
        }
    }

    public open(openIqcParameters: IOpenIQCParameters): void {
        if(!this.interQualConnectUrl) {
            this.logger.error('No URL provided to connect to IQC');
            return;
        } if(!this.javascriptSourceUrl) {
            this.logger.error('No URL provided to load javascript for IQC');
            return;
        }

        // unpack parameters
        const { auth, review, diagnosis, services, careDay } = openIqcParameters;
        const useRestrictedMode = openIqcParameters.useRestrictedMode || false;
        const allowReviewCopy = openIqcParameters.allowReviewCopy || false;
        const openReviewSummaryPdf = openIqcParameters.openReviewSummaryPdf || false;
        const splitCodes = openIqcParameters.splitCodes || false;
        const useDynamicSso = openIqcParameters.useDynamicSso || false;
        const hideSaveReviewButton = openIqcParameters.hideSaveReviewButton || false;

        this.auth = auth;
        this.review = review;
        this.diagnosis = diagnosis;
        this.services = services;
        this.careDay = careDay;

        const params: IPopupParameters = {};

        if(hideSaveReviewButton) {
            params.hideSaveReviewButton = true;
        }

        if(useRestrictedMode) {
            params.guidelineRestricted = true;
            params.newReview = {
                guidelineId: this.guidelineIds || '',
                bookview: false
            };
        }
        if(review?.identifier?.external) {
            params.reviewId = review.identifier.external;
            params.copyReview = review.complete && allowReviewCopy || false;
            params.openReviewSummaryPdf = openReviewSummaryPdf || false;
        }
        // TODO: Need to update the diagnosis code to handle bulk medical reviews for multiple codes.
        if(diagnosis?.definition?.code) {
            // If guidelines were found, perform medical review on diagnosis code that was searched instead
            params.diag = this.guidelineCode ?? diagnosis.definition?.code;
        }
        if(this.services?.length) {
            if(splitCodes) {
                let svcs: string[] = [];
                params.cptCodes = [];
                params.hcpcsCodes = [];
                this.services.forEach(svc => {
                    const foundMapping = svc.definition?.type?.mappings?.find(mapping => mapping.externalSystem?.code === EXTERNAL_SYSTEM_CODE);

                    // if no valid CPT/HCPCS mapping, do not send code to IQC
                    if(!foundMapping || !foundMapping.code || !svc.definition?.code || !Object.values(MAPPING_CODES).includes(foundMapping.code)) return;

                    // if code already has applicable criteria, do not send code to IQC
                    if(svc.medicalReview?.criteriaStatus && svc.medicalReview.criteriaStatus !== NOT_APPLICABLE_CRITERIA_STATUS) return;

                    // if status determination already made, do not send code to IQC
                    const isDetermination = svc.status?.determination?.isDetermination;
                    if(isDetermination) {
                        const determinationType = svc.status?.determination?.status?.determination?.result ?? '';
                        // Approved Determination if "approved" or "modified"
                        let isApprovedDetermination = APPROVED_DETERMINATION_TYPES.indexOf(determinationType) > -1;
                        if(isApprovedDetermination) return;
                    }

                    // finally sort the remaining codes we want to send into CPT and HCPCS arrays
                    foundMapping.code === MAPPING_CODES.CPT ? params.cptCodes!.push(svc.definition.code) : params.hcpcsCodes!.push(svc.definition.code);
                    svcs.push(svc.definition.code);
                });

                if(svcs.length) params.svc = [...new Set(svcs)].join(','); // search query string, set removes duplicates
            } else {
                params.svc = this.services[0].definition?.code || '';
            }

            //add serviceState and serviceDate if PHI is enabled and applicable
            if(this.showPHI) {
                // Service State
                if(auth?.provider?.servicing?.location?.facility?.address?.physical?.region?.code) params.serviceState = auth.provider.servicing.location.facility.address.physical.region.code;

                // Service Date, find the oldest authorized/start date to send to api. Skip if there are services with no date.
                const startDatesString: string[] = this.services.map(svc => svc.authorizedDate ?? 'None');
                if(!startDatesString.find(date => date === 'None')) {
                    const dates: Date[] = startDatesString.map(date => new Date(date));
                    const minDate = dates.reduce((a, b) => (a < b ? a : b));
                    params.serviceDate = formatIqcDate(new DateValue(minDate.toISOString()));
                }
                else if(auth?.initialDateOfService) params.serviceDate = formatIqcDate(new DateValue(auth.initialDateOfService));
                else if(auth?.serviceDate?.dateRange?.start) params.serviceDate = formatIqcDate(new DateValue(auth.serviceDate.dateRange.start));
            }
        }

        if(careDay?.id) { params.careDayId = careDay.id; }

        //add member data to query string
        if(this.showPHI && auth?.member && auth.member.name && auth.member.birthDate) {
            params.memberFirstName = auth.member.name.first || undefined;
            params.memberLastName = auth.member.name.last || undefined;
            params.memberDOB = formatIqcDate(new DateValue(auth.member.birthDate)); //Format: MM-dd-yyyy
            params.memberGender = mapIqcGenderCode(auth.member.gender?.designation?.identity); //Allowed values: MALE, FEMALE, or OTHER
            params.externalId = auth.member.identifier?.external || '';

            //add PayerID and PayerMemberID
            if(auth.provider?.servicing?.location?.facility?.identifier?.external) {
                const additionalIds: additionalId[] = [];
                additionalIds?.push({ type: 'PayerID', id: auth.provider.servicing.location.facility.identifier.external });
                additionalIds?.push({ type: 'PayerMemberID', id: auth.member.identifier?.external || '' });
                params.additionalIds = additionalIds;
            }
        }

        //add context parameters for medical review if PHI is enabled
        if(this.showPHI) {
            const primaryRefContext: IMRCollection = { type: 'MRCollection', name: 'PrimaryRef', value: auth?.id || '' };
            const secondaryRefContext: IMRCollection = { type: 'MRCollection', name: 'SecondaryRef', value: '' };

            if(diagnosis?.id || review?.diagnosis?.id) secondaryRefContext.value = `D${diagnosis?.id || review?.diagnosis?.id}`;
            else if(!!services?.length && services[0].id || review?.service?.id) secondaryRefContext.value = `S${services && services[0].id || review?.service?.id}`;

            if(primaryRefContext.value !== '' && secondaryRefContext.value !== '') params.contextParams = [primaryRefContext, secondaryRefContext];
        }

        // timestamp to avoid cache
        const query: Record<string, string> = {
            ts: String(new Date().getTime())
        };

        const queryString = new URLSearchParams(query).toString();
        const w = window.open('app/ext/iqc/iqc-popup.html?' + queryString, 'iqcPopup', 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
        if(!w) {
            this.logger.warn('Unable to open external iqc pop-up');
            return;
        }
        // set the window values here
        (<any>w)['Essette.IQC.parameters'] = params;
        (<any>w)['Essette.IQC.sourceUrl'] = this.javascriptSourceUrl;

        w.onload = async () => {
            const response = await this.getSAMLResponse(useDynamicSso ? auth : null); // if using dynamic SSO, SAML response requires auth information
            if(!response?.resultText) { throw new Error('[IQC] Error retrieving SAML token'); }
            w.document.title = this.i18n.tr('ui:titles.essette') + ' / ' + this.i18n.tr('ext:iqc.integration.$');
            w.document.getElementById('closeButton')!.textContent = this.i18n.tr('ui:actions.returnTo', { what: this.i18n.tr('ui:titles.essette') });
            const frame: HTMLIFrameElement = <HTMLIFrameElement>w.document.getElementById('iqconnect');
            if(frame.contentWindow) {
                const loadingMessage = this.i18n.tr('ui:labels.openingPleaseWait', { what: this.i18n.tr('ext:iqc.$long') });
                frame.contentWindow.document.write('<!DOCTYPE html><html><body>' + loadingMessage + '</body></html>');
                const f: HTMLFormElement = frame.contentWindow.document.createElement('form');
                const i: HTMLInputElement = frame.contentWindow.document.createElement('input');
                f.method = 'post';
                i.name = 'SAMLResponse';
                i.hidden = true;
                i.value = btoa(response.resultText || '');
                f.appendChild(i);
                f.action = this.interQualConnectUrl!;
                frame.contentWindow.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();
            }
        };
    }

    public async searchGuidelines(auth?: UM.Auth, codes?: ICodes[], useDynamicSso: boolean = false): Promise<void> {
        // If guideline iframe already exists, remove first
        this.removeIqcIFrame();

        if(!codes?.length) {
            this.logger.error('No codes to search for guidelines');
            return;
        }
        if(!this.interQualConnectUrl) {
            this.logger.error('No URL provided to connect to IQC');
            return;
        } if(!this.javascriptSourceUrl) {
            this.logger.error('No URL provided to load javascript for IQC');
            return;
        }

        const params: IIQFrameParameters = {};
        if(codes[0].modelCode === "CD.Diagnosis") {
            params.diagnosisCodes = codes.map(d => d.code ?? '');
        }
        else if(codes[0].modelCode === "CD.Service") {
            params.serviceCodes = codes.map(d => d.code ?? '');
            //add serviceState and serviceDate if PHI is enabled and applicable
            if(this.showPHI) {
                // Service State
                if(auth?.provider?.servicing?.location?.facility?.address?.physical?.region?.code) params.serviceState = auth.provider.servicing.location.facility.address.physical.region.code;

                // Service Date, find the oldest authorized/start date to send to api. Skip if there are services with no date.
                const startDatesString: string[] = codes.map(svc => svc.startDate ?? 'None');
                if(!startDatesString.find(date => date === 'None')) {
                    const dates: Date[] = startDatesString.map(date => new Date(date));
                    const minDate = dates.reduce((a, b) => (a < b ? a : b));
                    params.serviceDate = formatIqcDate(new DateValue(minDate.toISOString().substring(0, 10)));
                }
                else if(auth?.initialDateOfService) params.serviceDate = formatIqcDate(new DateValue(auth.initialDateOfService));
                else if(auth?.serviceDate?.dateRange?.start) params.serviceDate = formatIqcDate(new DateValue(auth.serviceDate.dateRange.start));
            }
        }

        if(!isNullOrUndefined(auth?.member?.age?.years)) {
            params.age = auth?.member?.age?.years.toString();
        }

        const ssoResponse = await this.getSAMLResponse(useDynamicSso ? auth : null);
        if(!ssoResponse?.resultText) { throw new Error('[IQC] Error retrieving SAMl token'); }
        params.samlAssertion = ssoResponse.resultText;

        // timestamp to avoid cache
        const query: Record<string, string> = {
            ts: String(new Date().getTime())
        };
        const queryString = new URLSearchParams(query).toString();

        let w: HTMLIFrameElement = document.createElement("iframe");
        w.setAttribute('id', 'iqc-iframe');
        w.setAttribute('src', './app/ext/iqc/iqc-search-guidelines.html?' + queryString);
        w.setAttribute('style', 'display:none;')

        document.body.appendChild(w);

        // set the window values here
        (<any>w).contentWindow['Essette.IQC.parameters'] = params;
        (<any>w).contentWindow['Essette.IQC.sourceUrl'] = this.javascriptSourceUrl;

        // if no guidelines found within the timeout parameters, emit search failed event so user can skip step
        const headlessGuidelineSearchTimeout = this.env.config.iqcHeadlessGuidelineSearchTimeoutSeconds;
        this.timeoutId = setTimeout(() => this.timeoutHeadlessGuidelineSearch(), headlessGuidelineSearchTimeout * 1000);

        if(!w) {
            this.logger.warn('Unable to search for IQC guidelines');
            this.events.publish(new AuthExternalSystemErrorEvent);

            // Remove IQC IFrame
            this.removeIqcIFrame();
        }
    }

    protected removeIqcIFrame() {
        const iqcIframe = document.getElementById('iqc-iframe');
        if(iqcIframe) {
            document.body.removeChild(iqcIframe);
        }
    }

    private timeoutHeadlessGuidelineSearch(): void {
        this.removeIqcIFrame();
        this.events.publish(new AuthIQCGuidelinesSearchTimeoutEvent());
    }

    private async handleEventMessage(event: MessageEvent): Promise<void> {
        if(typeof event.data === 'object') {
            if(this.auth) {
                if('UUID' in event.data) {
                    if(this.review) {
                        //update review
                        console.log('[InterQualOnline] Updating Review');
                        const saveReview: UM.AuthMedicalReview = {
                            id: this.review.id,
                            identifier: {
                                external: event.data.UUID
                            },
                            content: {
                                data: event.data.review_xml,
                                type: { contentType: "application/xml" }
                            },
                            complete: this.editedReview.complete ?? false
                        };

                        await this.updateMedicalReview(saveReview);
                        this.events.publish(new AuthExternalSystemChangeEvent);
                        this.events.publish(new AuthIQCMedicalReviewEvent);
                    }
                    else {
                        //insert review
                        console.log('[InterQualOnline] Inserting Review');
                        const saveReview: UM.AuthMedicalReview = {
                            auth: {
                                id: this.auth.id
                            },
                            identifier: {
                                external: event.data.UUID
                            },
                            externalSystem: {
                                code: 'IQC'
                            },
                            content: {
                                data: event.data.review_xml,
                                type: { contentType: "application/xml" }
                            },
                            complete: this.editedReview.complete ?? false
                        };

                        if(this.diagnosis) {
                            saveReview.diagnosis = {
                                id: this.diagnosis.id
                            };
                        }
                        if(this.services?.length) {
                            if(this.services.length === 1) {
                                saveReview.service = {
                                    id: this.services[0].id
                                };
                            } else if(this.services.length > 1) {
                                saveReview.authServices = [];
                                this.services.forEach(svc => {
                                    if(svc.id) saveReview.authServices?.push({ id: svc.id });
                                });
                            }
                        }
                        if(this.careDay) {
                            saveReview.careDay = { id: this.careDay.id };
                        }

                        const response = await this.insertMedicalReview(saveReview);
                        if(response.isSuccessful) {
                            this.review = deepClone(saveReview);
                            this.review.id = response.id;
                            this.events.publish(new AuthExternalSystemChangeEvent);
                            this.events.publish(new AuthIQCMedicalReviewEvent);
                        }
                    }
                }
                else if('canSave' in event.data) {
                    this.editedReview.complete = !event.data.canSave;
                }
            }
            // Headless guideline search, called before opening IQC for Medical Review
            if('members' in event.data) {
                // Handle Headless Guideline Search from IQC
                clearTimeout(this.timeoutId);
                const guidelinesFound = event.data.members ?? [];
                this.guidelineIds = guidelinesFound.map((guideLine: any) => guideLine.id).toString() ?? '';
                this.guidelineCode = event.data?.code;
                this.events.publish(new AuthIQCGuidelinesSearchEvent(guidelinesFound));
                // Remove IQC IFrame
                this.removeIqcIFrame();
            }

            console.log(event.data);
        }
        else if(event.data === 'CLOSING') {
            this.events.publish(new AuthExternalSystemChangeEvent);

            //window.removeEventListener('message', this.listener, false);
            console.log('[InterQualOnline] ' + event.data);
        }
        else if(event.data.indexOf('ERROR') > -1) {
            this.events.publish(new AuthExternalSystemErrorEvent);

            // Remove IQC IFrame
            this.removeIqcIFrame();
        }
    }

    ////****************** Service Calls ********************************************////
    @api.apiCall
    private async getSAMLResponse(auth: UM.Auth | null = null): Promise<api.ApiPostResponse> {
        let action: IApiRequest;
        if(auth) { // if auth provided, set up for using dynamic SSO
            const facilityPrimaryId = auth?.provider?.requesting?.location?.facility?.identifier?.external;
            const facilityPrimaryName = auth?.provider?.requesting?.location?.facility?.description;
            if(!facilityPrimaryId || !facilityPrimaryName) {
                this.logger.error('Facility Primary ID and Facility Primary Name required for Dynamic SSO with IQC.');
            }
            let dynamicSsoParameters = `${facilityPrimaryId}|||${facilityPrimaryName}`;
            const healthPlanCode = auth?.member?.plan?.healthPlan?.code;
            if(healthPlanCode) dynamicSsoParameters += `|||${healthPlanCode}`;
            const lineOfBusinessCode = auth?.member?.plan?.lineOfBusiness?.code;
            if(lineOfBusinessCode) dynamicSsoParameters += `|||${lineOfBusinessCode}`;
            action = new api.ApiAction('singlesignon', 'account', { code: 'IQC_Dynamic', description: dynamicSsoParameters });
        } else { // otherwise use standard SSO
            action = new api.ApiAction('singlesignon', 'account', { code: 'IQC' });
        }
        return await this.api.post(action);
    }

    @api.apiCall
    public async insertMedicalReview(review: UM.AuthMedicalReview): Promise<api.ApiPostResponse> {
        const actionName = review.authServices?.length ? 'bulkInsert' : 'insert';
        const action = new api.ApiAction(actionName, 'um/auths/medicalReviews', review);
        return await this.api.post(action);
    }

    @api.apiCall
    public async updateMedicalReview(review: UM.AuthMedicalReview): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('update', `um/auths/medicalReviews/${review.id}`, review);
        return await this.api.post(action);
    }

    @api.apiCall
    public async loadSystemSettings(): Promise<IApiSettings> {
        try {
            const response = await this.api.get(this.api.settings(`IQC/Systems/`));
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }
}

// Combined Diagnosis and Service Codes
export interface ICodes {
    modelCode: string;  //CD.Diagnosis or CD.Service
    code: string;
    description: string;
    type: string;  //External System Mapping
    criteriaStatus: string; //Criteria Status from Medical Review
    determination: string;
    isApprovedDetermination: boolean;
    startDate?: string | null;
}
